/**
 * Armazenamento em OPFS — Origin Private File System (PLAN §16).
 *
 * Escolhido como primário porque permite **escrita incremental**: um vídeo de 30 MB
 * é gravado pedaço a pedaço direto no disco, sem passar inteiro pela memória. Isso
 * importa no iPhone, onde uma PWA pode ser encerrada por pressão de memória.
 *
 * Estrutura:
 *   /media/<sign-id>.mp4   arquivos concluídos e validados
 *   /temp/<sign-id>.mp4    downloads em andamento
 */
import {
  fileNameFor,
  isQuotaError,
  keyFromFileName,
  StorageQuotaError,
  StorageWriteError,
  type OfflineFileStore,
  type WriteOptions,
} from '@/storage/types';

const MEDIA_DIR = 'media';
const TEMP_DIR = 'temp';

export class OpfsFileStore implements OfflineFileStore {
  readonly id = 'opfs';

  isSupported(): boolean {
    return (
      typeof navigator !== 'undefined' &&
      typeof navigator.storage?.getDirectory === 'function' &&
      typeof FileSystemFileHandle !== 'undefined'
    );
  }

  private async root(): Promise<FileSystemDirectoryHandle> {
    return navigator.storage.getDirectory();
  }

  /** Diretórios são criados sob demanda (F10-T02, F10-T03). */
  private async dir(name: string, create = true): Promise<FileSystemDirectoryHandle> {
    return (await this.root()).getDirectoryHandle(name, { create });
  }

  private async fileHandle(
    dirName: string,
    key: string,
    create: boolean,
  ): Promise<FileSystemFileHandle | null> {
    try {
      const directory = await this.dir(dirName, create);
      return await directory.getFileHandle(fileNameFor(key), { create });
    } catch (error) {
      if ((error as DOMException).name === 'NotFoundError') return null;
      throw error;
    }
  }

  async write(key: string, source: Response | Blob, options: WriteOptions = {}): Promise<void> {
    const { onProgress, signal } = options;

    const tempHandle = await this.fileHandle(TEMP_DIR, key, true);
    if (!tempHandle) throw new StorageWriteError('Não foi possível criar o arquivo temporário.');

    let bytesWritten = 0;

    const declaredSize =
      source instanceof Blob ? source.size : Number(source.headers.get('content-length'));
    const totalBytes =
      options.expectedBytes ?? (declaredSize > 0 && Number.isFinite(declaredSize) ? declaredSize : undefined);

    let writable: FileSystemWritableFileStream | undefined;

    try {
      writable = await tempHandle.createWritable();

      if (source instanceof Blob) {
        await writable.write(source);
        bytesWritten = source.size;
        onProgress?.({ bytesWritten, ...(totalBytes ? { totalBytes } : {}) });
      } else {
        const reader = source.body?.getReader();
        if (!reader) throw new StorageWriteError('A resposta não tem corpo legível.');

        for (;;) {
          if (signal?.aborted) throw new DOMException('Download cancelado', 'AbortError');

          const { done, value } = await reader.read();
          if (done) break;
          if (!value) continue;

          await writable.write(value);
          bytesWritten += value.byteLength;
          onProgress?.({ bytesWritten, ...(totalBytes ? { totalBytes } : {}) });
        }
      }

      await writable.close();
      writable = undefined;

      // Promoção só depois da escrita completa: é isso que impede um arquivo
      // interrompido de aparecer como disponível (F12-T09).
      await this.promote(key);
    } catch (error) {
      // Fecha o stream e descarta o temporário; um parcial não pode sobreviver.
      if (writable) await writable.abort().catch(() => undefined);
      await this.removeFrom(TEMP_DIR, key).catch(() => undefined);

      if (isQuotaError(error)) {
        throw new StorageQuotaError(
          'Não há espaço suficiente no dispositivo para salvar este vídeo.',
          { cause: error },
        );
      }
      if (error instanceof DOMException && error.name === 'AbortError') throw error;

      throw new StorageWriteError(`Falha ao gravar o arquivo: ${(error as Error).message}`, {
        cause: error,
      });
    }
  }

  /** Move o arquivo de `temp/` para `media/`, substituindo o anterior. */
  private async promote(key: string): Promise<void> {
    const tempDirectory = await this.dir(TEMP_DIR);
    const mediaDirectory = await this.dir(MEDIA_DIR);
    const name = fileNameFor(key);

    const handle = await tempDirectory.getFileHandle(name);

    // `move` é o caminho atômico, mas ainda não existe em todo navegador.
    if (typeof handle.move === 'function') {
      await handle.move(mediaDirectory, name);
      return;
    }

    // Fallback: copia e remove. A janela de inconsistência é o intervalo entre as
    // duas operações, e ela deixa o arquivo em media/ — nunca ausente dos dois.
    const file = await handle.getFile();
    const target = await mediaDirectory.getFileHandle(name, { create: true });
    const writable = await target.createWritable();
    await writable.write(file);
    await writable.close();
    await tempDirectory.removeEntry(name).catch(() => undefined);
  }

  async exists(key: string): Promise<boolean> {
    const handle = await this.fileHandle(MEDIA_DIR, key, false);
    if (!handle) return false;

    // Um handle existente com arquivo de tamanho zero é resíduo, não conteúdo.
    const file = await handle.getFile();
    return file.size > 0;
  }

  async read(key: string): Promise<Blob> {
    const handle = await this.fileHandle(MEDIA_DIR, key, false);
    if (!handle) throw new StorageWriteError(`Arquivo não encontrado: ${key}`);
    return handle.getFile();
  }

  async getSize(key: string): Promise<number | null> {
    const handle = await this.fileHandle(MEDIA_DIR, key, false);
    if (!handle) return null;
    return (await handle.getFile()).size;
  }

  private async removeFrom(dirName: string, key: string): Promise<void> {
    const directory = await this.dir(dirName, false).catch(() => null);
    if (!directory) return;
    await directory.removeEntry(fileNameFor(key)).catch(() => undefined);
  }

  async remove(key: string): Promise<void> {
    await this.removeFrom(MEDIA_DIR, key);
    await this.removeFrom(TEMP_DIR, key);
  }

  async list(): Promise<string[]> {
    const directory = await this.dir(MEDIA_DIR, false).catch(() => null);
    if (!directory) return [];

    const keys: string[] = [];
    for await (const name of directory.keys()) keys.push(keyFromFileName(name));
    return keys;
  }

  async totalSize(): Promise<number> {
    const directory = await this.dir(MEDIA_DIR, false).catch(() => null);
    if (!directory) return 0;

    let total = 0;
    for await (const handle of directory.values()) {
      if (handle.kind !== 'file') continue;
      total += (await (handle as FileSystemFileHandle).getFile()).size;
    }
    return total;
  }

  async clearTemp(): Promise<number> {
    const directory = await this.dir(TEMP_DIR, false).catch(() => null);
    if (!directory) return 0;

    const names: string[] = [];
    for await (const name of directory.keys()) names.push(name);
    for (const name of names) await directory.removeEntry(name).catch(() => undefined);

    return names.length;
  }
}
